/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {Box, Button, Stack, Flex, Text} from '@salesforce/retail-react-app/app/components/shared/ui'
import usePromoCodeFields from '../usePromoCodeFields/index'
import Field from '../../field/index'
import {
    applyButtonStyle,
    couponStyle,
    removeStyle,
    tagIconStyle
} from '../../order-summary/index.style'
import {errorMessageStyle} from '../../product-item/index.style'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {extractCouponsFromBasket} from '../../../utills/couponUtils'

const PromoCodeFields = ({form, prefix = '', basket, removePromoCode, ...props}) => {
    const fields = usePromoCodeFields({form, prefix})

    const code = form.watch('code')

    const isDisabled = !code || (typeof code === 'string' && code.trim().length === 0)

    const handleSubmit = (e) => {
        if (isDisabled) {
            e.preventDefault()
            e.stopPropagation()
            return false
        }
    }

    // Extract applied coupons from basket
    const allCoupons = basket ? extractCouponsFromBasket(basket) : []

    return (
        <Box aria-labelledby="code-feedback" {...props}>
            <Field
                styles={{...errorMessageStyle, m: 0}}
                inputProps={{flex: 1, mr: 2}}
                {...fields.code}
            >
                <Button
                    sx={applyButtonStyle}
                    type="submit"
                    isLoading={form.formState.isSubmitting}
                    disabled={isDisabled}
                    onClick={handleSubmit}
                    aria-disabled={isDisabled}
                    variant="red-hue"
                >
                    <FormattedMessage defaultMessage="Apply" id="promo_code_fields.button.apply" />
                </Button>
            </Field>

            {/* Display applied coupons after the promotion field and below any errors */}
            {basket?.couponItems?.length > 0 && (
                <Stack mt="11px">
                    {allCoupons
                        .filter((item) => item.statusCode === 'applied')
                        .map((item) => (
                            <Flex
                                key={item.couponItemId}
                                alignItems="center"
                                justifyContent="space-between"
                                gap="4px"
                            >
                                <Flex alignItems="center">
                                    {item?.couponCode && (
                                        <Box
                                            backgroundImage={`url(${getAssetUrl(
                                                'static/img/cart/promotion-tag.png'
                                            )})`}
                                            sx={tagIconStyle}
                                        />
                                    )}
                                    <Text
                                        as="span"
                                        sx={couponStyle}
                                    >{`${item?.couponCode} applied`}</Text>
                                    {!basket.orderNo && (
                                        <Button
                                            variant="link"
                                            sx={{...removeStyle, marginLeft: '15px'}}
                                            onClick={() => removePromoCode(item.couponItemId)}
                                        >
                                            <FormattedMessage
                                                defaultMessage="Remove"
                                                id="order_summary.action.remove_promo"
                                            />
                                        </Button>
                                    )}
                                </Flex>
                                <Flex>
                                    <Text sx={{m: 0}} key={item?.couponItemId}>
                                        <FormattedNumber
                                            style="currency"
                                            currency={basket?.currency}
                                            value={item?.couponValue}
                                        />
                                    </Text>
                                </Flex>
                            </Flex>
                        ))}
                </Stack>
            )}
        </Box>
    )
}

PromoCodeFields.propTypes = {
    /** Object returned from useForm */
    form: PropTypes.object.isRequired,

    /** Optional prefix for field names */
    prefix: PropTypes.string,

    /** Basket object containing coupon information */
    basket: PropTypes.object,

    /** Function to remove promo codes */
    removePromoCode: PropTypes.func
}

export default PromoCodeFields
